import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router";

import variants from "./buttonVariants.module.css";

/**
 * Visual style, shared with {@link RobotoButton} via `buttonVariants.module.css`.
 * Grows alongside RobotoButton's union.
 */
export type ButtonLinkVariant = "primary" | "secondary" | "ghost";

/** Control density, shared with {@link RobotoButton}. */
export type ButtonLinkSize = "sm" | "md" | "lg";

/**
 * Mirrors {@link RobotoButtonProps}: an intentional, tight prop surface rather
 * than all of react-router's `LinkProps`. Link-specific extras (`replace`,
 * `state`, `reloadDocument`, `preventScrollReset`, `relative`) are omitted
 * until a call site needs them. `loading`, `disabled` and `type` are absent by
 * design — they are meaningless for a navigation link.
 */
export interface ButtonLinkProps extends React.AriaAttributes {
  /**
   * Destination: an in-app route path, or an absolute URL when `external`.
   */
  href: string;
  /**
   * Link to a site outside the app. Renders a plain `<a>` instead of a router
   * link, opens in a new tab, and sets `rel="noopener noreferrer"`.
   * @default false
   */
  external?: boolean;
  /**
   * Visual style. @default "primary"
   */
  variant?: ButtonLinkVariant;
  /**
   * Control density. @default "md"
   */
  size?: ButtonLinkSize;
  /**
   * Icon rendered before the children. Accepts any React element (e.g. a
   * lucide or MUI icon). The variant sizes and colors it to match the label.
   */
  icon?: React.ReactNode;
  children?: React.ReactNode;
  /** Extra class(es) for call-site layout/positioning. */
  className?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  id?: string;
  title?: string;
}

const variantClass: Record<ButtonLinkVariant, string> = {
  primary: variants.primary,
  secondary: variants.secondary,
  ghost: variants.ghost,
};

const sizeClass: Record<ButtonLinkSize, string> = {
  sm: variants.sizeSm,
  md: variants.sizeMd,
  lg: variants.sizeLg,
};

/**
 * A navigation link that looks like a button — a real `<a>` wearing the shared
 * button-variant styling.
 *
 * @example
 * ```tsx
 * <ButtonLink href={Goto.route.CreateDataset} variant="primary" icon={<PlusIcon />}>
 *   New Dataset
 * </ButtonLink>
 *
 * <ButtonLink href="https://docs.roboto.ai" external>
 *   Read the docs
 * </ButtonLink>
 * ```
 */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  function ButtonLink(
    {
      href,
      external = false,
      variant = "primary",
      size = "md",
      icon,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const hasLabel = children !== undefined && children !== null;
    const classes = classNames(
      variantClass[variant],
      sizeClass[size],
      { [variants.iconOnly]: !hasLabel },
      className,
    );
    const content = (
      <>
        {icon}
        {children}
      </>
    );

    if (external) {
      return (
        <a
          ref={ref}
          href={href}
          className={classes}
          target="_blank"
          // noopener denies the new tab access to window.opener; noreferrer
          // also withholds the Referer header.
          rel="noopener noreferrer"
          {...props}
        >
          {content}
        </a>
      );
    }

    return (
      <Link ref={ref} to={href} className={classes} {...props}>
        {content}
      </Link>
    );
  },
);
